import { ChartBar, Warning, WarningCircle, Plus, ArrowClockwise, Minus, Lightning, Database } from "@phosphor-icons/react";

const SOURCE_LABEL = {
    yellow: "Yellow",
    finda: "Finda",
    localist: "Localist",
    google: "Web Search",
};

export default function MetricsPanel({ run }) {
    if (!run) return null;
    const m = run.metrics || {};
    const counts = m.source_counts || {};
    const failures = m.failures || {};
    const warnings = m.warnings || [];
    const rateLimited = failures.rate_limited_sources || [];

    const total = Object.values(counts).reduce((a, b) => a + (b || 0), 0);
    const diff = run.diff || {};

    return (
        <div className="panel" data-testid="metrics-panel">
            <div className="panel-header">
                <div className="flex items-center gap-2">
                    <ChartBar size={16} weight="bold" />
                    <span className="label-kicker">Source Breakdown</span>
                </div>
                <span className="mono text-xs text-[#52525b]">
                    {m.final_exportable ?? run.result_count ?? 0} exportable
                </span>
            </div>

            {/* Diff summary — NEW / UPDATED / REMOVED */}
            {(typeof diff.new === "number") && (
                <div
                    className="px-5 py-3 border-b border-[#e4e4e7] grid grid-cols-2 md:grid-cols-4 gap-3"
                    data-testid="diff-summary"
                >
                    <DiffStat
                        icon={<Plus size={14} weight="bold" />}
                        label="New providers"
                        value={diff.new || 0}
                        color="#002FA7"
                        testId="diff-new-count"
                    />
                    <DiffStat
                        icon={<ArrowClockwise size={14} weight="bold" />}
                        label="Updated"
                        value={diff.updated || 0}
                        color="#b07000"
                        testId="diff-updated-count"
                    />
                    <DiffStat
                        icon={<Minus size={14} weight="bold" />}
                        label="Removed"
                        value={diff.removed || 0}
                        color="#ff3333"
                        testId="diff-removed-count"
                    />
                    <DiffStat
                        icon={null}
                        label="High priority"
                        value={diff.high_priority || 0}
                        color="#000"
                        testId="diff-high-count"
                    />
                </div>
            )}

            {m.ddg_rate_limited && (
                <div
                    className="px-5 py-3 border-b border-[#e4e4e7] flex items-start gap-2"
                    style={{ background: "#fff7e0" }}
                    data-testid="rate-limit-banner"
                >
                    <Warning size={18} weight="fill" color="#b07000" />
                    <div className="text-sm">
                        <div className="font-medium">Open web search rate-limited</div>
                        <div className="text-[#52525b] text-xs mt-0.5">
                            Continuing with directory sources only. Results from Yellow and
                            Finda are unaffected.
                        </div>
                    </div>
                </div>
            )}

            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                {["yellow", "finda", "localist", "google"].map((k) => {
                    const n = counts[k];
                    const isRateLimited = rateLimited.includes(k);
                    const hasData = typeof n === "number";
                    return (
                        <div
                            key={k}
                            className="border border-[#e4e4e7] p-3 flex flex-col gap-1"
                            data-testid={`source-stat-${k}`}
                        >
                            <span className="label-kicker" style={{ fontSize: 9 }}>
                                {SOURCE_LABEL[k]}
                            </span>
                            <span className="font-display text-2xl font-bold">
                                {hasData ? n : "—"}
                            </span>
                            <span className="mono text-[10px] text-[#52525b]">
                                {!hasData
                                    ? "not queried"
                                    : n === 0 && isRateLimited
                                    ? "rate-limited"
                                    : n === 0
                                    ? "no listings"
                                    : "listings found"}
                            </span>
                        </div>
                    );
                })}
            </div>

            <div className="px-5 pb-5 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                <StatRow
                    label="Raw collected"
                    value={total}
                    testid="raw-total"
                />
                <StatRow
                    label="Duplicates removed"
                    value={m.duplicates_removed ?? 0}
                    testid="duplicates-removed"
                />
                <StatRow
                    label="Websites unreachable"
                    value={failures.websites_unreachable ?? 0}
                    danger={(failures.websites_unreachable ?? 0) > 0}
                    testid="websites-unreachable"
                />
                <StatRow
                    label="No contact found"
                    value={failures.no_contact_found ?? 0}
                    warning={(failures.no_contact_found ?? 0) > 0}
                    testid="no-contact-found"
                />
                <StatRow
                    label="Final exportable"
                    value={m.final_exportable ?? run.result_count ?? 0}
                    success
                    testid="final-exportable"
                />
            </div>

            {warnings.length > 0 && (
                <div className="px-5 pb-5" data-testid="warnings-list">
                    <div className="label-kicker mb-2">Warnings</div>
                    <ul className="flex flex-col gap-1.5">
                        {warnings.map((w, i) => (
                            <li
                                key={`${i}-${(w || "").slice(0, 40)}`}
                                className="flex items-start gap-2 text-xs text-[#52525b]"
                            >
                                <WarningCircle
                                    size={14}
                                    weight="bold"
                                    className="shrink-0 mt-[2px] text-[#b07000]"
                                />
                                <span>{w}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Cache metrics */}
            <CacheStats cache={m.cache} />
        </div>
    );
}

function CacheStats({ cache }) {
    if (!cache || typeof cache !== "object") return null;
    const hits = cache.hits || 0;
    const misses = cache.misses || 0;
    const expired = cache.expired || 0;
    const failedRetried = cache.failed_retried || 0;
    const forceRefreshed = cache.force_refreshed || 0;
    const timeSavedMs = cache.time_saved_ms || 0;
    const seconds = Math.round(timeSavedMs / 1000);
    const total = hits + misses + expired + failedRetried + forceRefreshed;
    if (!total) return null;
    return (
        <div
            className="px-5 pb-5 pt-1 border-t border-[#e4e4e7]"
            data-testid="cache-stats"
        >
            <div className="flex items-center gap-2 mb-3 mt-3">
                <Database size={14} weight="bold" />
                <span className="label-kicker">Crawl Cache</span>
                {seconds > 0 && (
                    <span
                        className="mono text-[10px] px-2 py-0.5 bg-black text-white"
                        data-testid="time-saved"
                    >
                        ~{seconds}s saved
                    </span>
                )}
            </div>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3 text-xs">
                <CacheStat label="Hits" value={hits} color="#002FA7" testid="cache-hits" />
                <CacheStat label="Misses" value={misses} color="#000" testid="cache-misses" />
                <CacheStat label="Expired" value={expired} color="#b07000" testid="cache-expired" />
                <CacheStat label="Failed retry" value={failedRetried} color="#ff3333" testid="cache-failed-retried" />
                <CacheStat label="Force-refreshed" value={forceRefreshed} color="#52525b" testid="cache-force-refreshed" />
            </div>
        </div>
    );
}

function CacheStat({ label, value, color, testid }) {
    return (
        <div className="flex flex-col gap-0.5" data-testid={testid}>
            <span className="label-kicker" style={{ fontSize: 9 }}>
                {label}
            </span>
            <span className="mono text-lg font-bold" style={{ color }}>
                {value}
            </span>
        </div>
    );
}

function StatRow({ label, value, danger, warning, success, testid }) {
    return (
        <div className="flex flex-col gap-0.5" data-testid={testid}>
            <span className="label-kicker" style={{ fontSize: 9 }}>
                {label}
            </span>
            <span
                className={
                    "mono text-lg font-bold " +
                    (danger
                        ? "text-[#ff3333]"
                        : warning
                        ? "text-[#b07000]"
                        : success
                        ? "text-[#002FA7]"
                        : "")
                }
            >
                {value}
            </span>
        </div>
    );
}

function DiffStat({ icon, label, value, color, testId }) {
    const prefix =
        value > 0
            ? label.includes("New")
                ? "+"
                : label.includes("Updated")
                ? "~"
                : label.includes("Removed")
                ? "\u2212"
                : ""
            : "";
    return (
        <div className="flex flex-col gap-0.5" data-testid={testId}>
            <span
                className="label-kicker flex items-center gap-1"
                style={{ fontSize: 9, color: "#52525b" }}
            >
                {icon}
                {label}
            </span>
            <span className="mono text-xl font-bold" style={{ color }}>
                {prefix}
                {value}
            </span>
        </div>
    );
}

